import { useEffect, useRef, useState } from 'react';
import axiosInstance from '../services/api/axiosConfig';

export interface VideoJobCardState {
  status: string;
  progress_step: string | null;
  progress_percentage: number;
  url: string | null;
  video_id: number | null;
  video_model: string | null;
  aspect_ratio: string | null;
  duration_seconds: number | null;
  resolution: string | null;
  error_code: string | null;
  error_message: string | null;
}

export const VIDEO_JOB_POLL_MS = 5000;

/** 連続でこの回数だけ取得に失敗したらポーリングを打ち切る（無限リトライを防ぐ）。 */
export const MAX_POLL_FAILURES = 3;

const TERMINAL = new Set(['completed', 'failed', 'cancelled']);

/** ジョブが存在しない（404）ことが確定した失敗かどうか。リトライしても回復しない。 */
export function isJobNotFound(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false;
  return (err as { response?: { status?: number } }).response?.status === 404;
}

// 終端に達したジョブのプロセス内キャッシュ（履歴の再描画で叩き直さない）。
const terminalCache = new Map<string, VideoJobCardState>();

const EMPTY: VideoJobCardState = {
  status: 'pending', progress_step: null, progress_percentage: 0, url: null, video_id: null,
  video_model: null, aspect_ratio: null, duration_seconds: null, resolution: null, error_code: null, error_message: null,
};

interface JobDetailResponse {
  job?: Partial<{ status: string; progress_step: string | null; progress_percentage: number; video_model: string | null; aspect_ratio: string | null; duration_seconds: number | null; resolution: string | null; error_code: string | null; error_message: string | null }>;
  video?: { id?: number; url?: string } | null;
}

const toState = (data: JobDetailResponse, prev: VideoJobCardState): VideoJobCardState => ({
  status: data.job?.status ?? prev.status,
  progress_step: data.job?.progress_step ?? null,
  progress_percentage: typeof data.job?.progress_percentage === 'number' ? data.job.progress_percentage : prev.progress_percentage,
  url: data.video?.url ?? null,
  video_id: typeof data.video?.id === 'number' ? data.video.id : null,
  video_model: data.job?.video_model ?? prev.video_model,
  aspect_ratio: data.job?.aspect_ratio ?? prev.aspect_ratio,
  duration_seconds: data.job?.duration_seconds ?? prev.duration_seconds,
  resolution: data.job?.resolution ?? prev.resolution,
  error_code: data.job?.error_code ?? null,
  error_message: data.job?.error_message ?? null,
});

/**
 * 動画ジョブカード用: マウント時に GET /videos/jobs/{id} を 1 回読み（署名 URL の再取得）、
 * 進行中なら VIDEO_JOB_POLL_MS 間隔で完成まで追う。終端はキャッシュする。
 * 取得失敗は MAX_POLL_FAILURES 回まで同じ間隔でリトライし、超えたら打ち切る（404 は即打ち切り）。
 */
export function useVideoJobCard(
  jobId: string | null | undefined,
  enabled: boolean,
  fallback: Partial<VideoJobCardState> = {}
): { state: VideoJobCardState; loading: boolean; error: boolean; gaveUp: boolean } {
  const cached = jobId ? terminalCache.get(jobId) : undefined;
  const [state, setState] = useState<VideoJobCardState>(cached ?? { ...EMPTY, ...fallback });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  // 404 か連続失敗の上限でポーリングを打ち切ったか（カードが「状態不明」を出す判断に使う）。
  const [gaveUp, setGaveUp] = useState(false);
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    if (!enabled || !jobId) return;
    if (terminalCache.has(jobId)) {
      setState(terminalCache.get(jobId) as VideoJobCardState);
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let failures = 0;
    setGaveUp(false);

    const fetchOnce = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get(`/videos/jobs/${jobId}`);
        if (cancelled) return;
        failures = 0;
        const next = toState(response.data as JobDetailResponse, stateRef.current);
        setState(next);
        setError(false);
        if (TERMINAL.has(next.status)) {
          terminalCache.set(jobId, next);
          return;
        }
        timer = setTimeout(fetchOnce, VIDEO_JOB_POLL_MS);
      } catch (err) {
        if (cancelled) return;
        setError(true);
        // 404 は回復しないので即打ち切り。それ以外は上限までリトライする。
        if (isJobNotFound(err)) {
          setGaveUp(true);
          return;
        }
        failures += 1;
        if (failures >= MAX_POLL_FAILURES) {
          setGaveUp(true);
          return;
        }
        timer = setTimeout(fetchOnce, VIDEO_JOB_POLL_MS);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void fetchOnce();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [jobId, enabled]);

  return { state, loading, error, gaveUp };
}

// テスト用: キャッシュをクリアする。
export function __clearVideoJobCache(): void {
  terminalCache.clear();
}
